import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { format, isToday } from "date-fns";
import { enUS, es, fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import { AgendaDayStrip, AgendaDayTimeline } from "./AgendaDay";
import { AgendaMonth } from "./AgendaMonth";
import { AgendaViewSwitch } from "./AgendaViewSwitch";
import { AgendaWeek } from "./AgendaWeek";
import {
  agendaDays,
  agendaRange,
  formatAgendaDate,
  groupByDay,
  parseAgendaDate,
  parseAgendaView,
  shiftAgendaDate,
  type AgendaView,
} from "./agendaModel";
import { dayKey } from "./mobileModel";
import { useAgendaEntries } from "./useAgendaEntries";

const dateFnsLocale = (locale: string) =>
  locale.startsWith("fr") ? fr : locale.startsWith("en") ? enUS : es;

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

const STEP_LABELS: Record<AgendaView, [string, string]> = {
  day: ["crm.mobile.day_previous", "crm.mobile.day_next"],
  week: ["crm.mobile.week_previous", "crm.mobile.week_next"],
  month: ["crm.mobile.month_previous", "crm.mobile.month_next"],
};

/**
 * Schedule with Day / Week / Month views. View and date live in the URL
 * (`/agenda?view=week&date=2026-11-02`) so reloads and links keep them.
 */
export const MobileAgenda = () => {
  const translate = useTranslate();
  const [params, setParams] = useSearchParams();
  const view = parseAgendaView(params.get("view"));
  const dateParam = params.get("date");
  const date = useMemo(() => parseAgendaDate(dateParam), [dateParam]);

  const navigate = (nextView: AgendaView, nextDate: Date) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("view", nextView);
        next.set("date", formatAgendaDate(nextDate));
        return next;
      },
      { replace: true },
    );

  return (
    <>
      <MobileHeader>
        {/* Desktop has the sidebar and breadcrumb: no back chevron there. */}
        <div className="md:hidden">
          <MobileBackButton to="/" />
        </div>
        <h1 className="flex-1 text-xl font-semibold tracking-tight">
          {translate("crm.mobile.agenda")}
        </h1>
      </MobileHeader>
      <MobileContent>
        <AgendaBody view={view} date={date} onNavigate={navigate} />
      </MobileContent>
    </>
  );
};

interface AgendaBodyProps {
  view: AgendaView;
  date: Date;
  onNavigate: (view: AgendaView, date: Date) => void;
}

/**
 * The schedule itself (view switch, date stepper and the day, week or month
 * view), driven by its owner: the /agenda page keeps view and date in the
 * URL, the top-bar agenda window in its own state.
 */
export const AgendaBody = ({
  view,
  date,
  onNavigate: navigate,
}: AgendaBodyProps) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const dfLocale = dateFnsLocale(locale);

  // Day view keeps its week strip, so it loads the whole week too.
  const fetchView = view === "day" ? "week" : view;
  const range = agendaRange(fetchView, date);
  const { entries, pending } = useAgendaEntries(range.start, range.end);
  const byDay = useMemo(
    () => groupByDay(entries, (entry) => dayKey(entry.start)),
    [entries],
  );
  const days = agendaDays(fetchView, date);
  const [previousLabel, nextLabel] = STEP_LABELS[view];
  const openDay = (day: Date) => navigate("day", day);

  const title =
    view === "day"
      ? capitalize(format(date, "EEEE, d MMMM", { locale: dfLocale }))
      : view === "week"
        ? `${format(range.start, "d MMM", { locale: dfLocale })} – ${format(
            range.end,
            "d MMM yyyy",
            { locale: dfLocale },
          )}`
        : capitalize(format(date, "LLLL yyyy", { locale: dfLocale }));

  return (
    <>
      <div className="mb-4 flex flex-col gap-4 md:flex-row-reverse md:items-center md:justify-between">
        <AgendaViewSwitch
          value={view}
          onChange={(next) => navigate(next, date)}
        />
        <div className="flex min-w-0 items-center justify-between gap-2 md:justify-start md:gap-4">
          <p
            className="min-w-0 truncate text-lg font-semibold"
            aria-live="polite"
          >
            {title}
          </p>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-full px-3"
              disabled={isToday(date)}
              onClick={() => navigate(view, new Date())}
            >
              {translate("crm.mobile.agenda_today")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="rounded-full"
              aria-label={translate(previousLabel)}
              onClick={() => navigate(view, shiftAgendaDate(view, date, -1))}
            >
              <ChevronLeft />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="rounded-full"
              aria-label={translate(nextLabel)}
              onClick={() => navigate(view, shiftAgendaDate(view, date, 1))}
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      </div>
      {view === "day" ? (
        <AgendaDayStrip
          days={days}
          selected={date}
          busyKeys={new Set(byDay.keys())}
          dfLocale={dfLocale}
          onSelect={openDay}
        />
      ) : null}
      {pending ? (
        <Skeleton className="h-40 w-full rounded-4xl" />
      ) : view === "day" ? (
        <AgendaDayTimeline
          day={date}
          entries={byDay.get(dayKey(date)) ?? []}
          locale={locale}
        />
      ) : view === "week" ? (
        <AgendaWeek
          days={days}
          byDay={byDay}
          dfLocale={dfLocale}
          locale={locale}
          onOpenDay={openDay}
        />
      ) : (
        <AgendaMonth
          days={days}
          date={date}
          byDay={byDay}
          dfLocale={dfLocale}
          locale={locale}
          onOpenDay={openDay}
        />
      )}
    </>
  );
};
